/**
 * ============================================================================
 * VINPOP — Food Pairing Filter
 * ============================================================================
 *
 * Adds food-category filter buttons to the food-pairing collection page.
 * Users click food icons (meat, fish, cheese, etc.) to filter wines that
 * pair well with that food type. Uses product tags for matching.
 *
 * Loaded from: layout/theme.liquid (global, deferred)
 * Used on: /collections/food-pairing
 * ============================================================================
 */

// Flag to prevent re-execution loops
let isInitializing = false;
let hasAppliedRecommendations = false;
let hasStartedFoodPairingFilter = false;

function isMyWinePlaylistPage(pathname) {
  // Support localized storefront URLs like /en/collections/my-wine-playlist
  // and /pt-pt/collections/my-wine-playlist, with or without a trailing slash.
  return /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/collections\/my-wine-playlist\/?$/i.test(pathname);
}

// Main initialization function
function initFoodPairingFilter() {
  if (hasStartedFoodPairingFilter && hasAppliedRecommendations) {
    initializeFilterButtons();
    initializePlaylistRatingFilterButtons();
    initializeFoodPairingAccordion();
    applyCurrentFilter();
    return;
  }

  if (isInitializing) {
    // console.log('⏭️ Already initializing, skipping...');
    return;
  }
  
  isInitializing = true;
  hasStartedFoodPairingFilter = true;
  
  function getTranslation(key) {
    const translations = window.theme && window.theme.translations ? window.theme.translations : {};
    const translationKey = 'wine_recommendations_' + key;
    return translations[translationKey] || key;
  }

  // IMPORTANT: Run ONLY on /collections/my-wine-playlist without special parameters
  const pathname = window.location.pathname;
  const urlParams = new URLSearchParams(window.location.search);
  
  // Check if we're exactly on /collections/my-wine-playlist
  const isWineCollection = isMyWinePlaylistPage(pathname);
  
  if (!isWineCollection) {
    // console.log('⏭️ Food pairing filter skipped - not on /collections/my-wine-playlist page');
    return;
  }
  
  // Skip if special parameters are present (other features handling the page)
  const hasSourceProduct = urlParams.get('source_product') || urlParams.get('main-product');
  const inTasteProfileMode = urlParams.get('taste_profile') === 'true';
  
  if (hasSourceProduct) {
    // console.log('⏭️ Food pairing filter skipped - similar wines mode active');
    return;
  }
  
  if (inTasteProfileMode) {
    // console.log('⏭️ Food pairing filter skipped - taste profile quiz mode active');
    return;
  }
  
  // console.log('✅ Food pairing filter active on:', pathname);

  // Get customer ID
  const getCustomerId = () => {
    const metaTag = document.querySelector('meta[name="customer-id"]');
    return metaTag ? metaTag.getAttribute('content') : null;
  };

  const customerId = getCustomerId();
  const friendId = String(urlParams.get('friend_id') || '').trim();

  function getMetaContent(name) {
    const meta = document.querySelector(`meta[name="${name}"]`);
    return meta ? String(meta.getAttribute('content') || '').trim() : '';
  }

  function getFriendsApiBase() {
    const fromMeta = getMetaContent('friends-api-url');
    return (fromMeta || 'https://api.vinpop.fr/api/friends').replace(/\/+$/, '');
  }

  function buildFriendsAuthHeaders() {
    const timestamp = getMetaContent('friends-auth-timestamp');
    const signature = getMetaContent('friends-auth-signature');
    if (!customerId || !timestamp || !signature) {
      throw new Error('friends_auth_unavailable');
    }

    return {
      'Content-Type': 'application/json',
      'X-Customer-Id': customerId,
      'X-Timestamp': timestamp,
      'X-Signature': signature
    };
  }

  function getFriendFromRow(row) {
    if (Array.isArray(row)) {
      return String(row[0]) === friendId ? {
        email: String(row[1] || '').trim(),
        name: String(row[2] || '').trim()
      } : null;
    }

    if (row && typeof row === 'object') {
      return String(row.id || '') === friendId ? {
        email: String(row.email || '').trim(),
        name: String(row.name || '').trim()
      } : null;
    }

    return null;
  }

  async function updateFriendPlaylistHeading() {
    if (!friendId || window.__vinpopFriendPlaylistHeadingId === friendId) {
      return;
    }

    try {
      const response = await fetch(`${getFriendsApiBase()}/list`, {
        method: 'GET',
        headers: buildFriendsAuthHeaders(),
        credentials: 'omit',
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error('friends_list_failed');
      }

      const rows = await response.json();
      const friend = Array.isArray(rows)
        ? rows.map(getFriendFromRow).find(Boolean)
        : null;

      if (!friend || !friend.name) {
        return;
      }

      const heading = document.querySelector('h1.heading-feature.heading-style, h1.heading-feature');
      if (!heading) {
        return;
      }

      const isEnglish = (document.documentElement.lang || '').toLowerCase().indexOf('en') === 0;
      heading.textContent = isEnglish
        ? `${friend.name}'s wine playlist`
        : `Wijnplaylist van ${friend.name}`;
      window.__vinpopFriendPlaylistHeadingId = friendId;
    } catch (error) {
      console.warn('Friends playlist heading update failed:', error);
    }
  }

  async function loadPlaylistRatings() {
    if (!friendId) {
      const ratingsResponse = await fetch(`https://api.vinpop.fr/api/ratings/getAll?customerId=${customerId}`, {
        headers: buildFriendsAuthHeaders()
      });
      return ratingsResponse.json();
    }

    const ratingsResponse = await fetch(`${getFriendsApiBase()}/playlist`, {
      method: 'POST',
      headers: buildFriendsAuthHeaders(),
      credentials: 'omit',
      mode: 'cors',
      body: JSON.stringify({ id: friendId })
    });

    if (!ratingsResponse.ok) {
      if (ratingsResponse.status === 401) {
        showFriendPlaylistAccessDenied();
        return [];
      }
      throw new Error('friends_playlist_failed');
    }

    return ratingsResponse.json();
  }
  
  // ──────────────────────────────────────────────
  // DEMO MODE: When no customer is logged in, inject demo data
  // to showcase the recommendation engine with pre-set loved wines
  // ──────────────────────────────────────────────
  const isDemoMode = !customerId;

  let pairingsData = [];
  let allApiResponses = [];
  let lovedProductIds = [];
  let excellentProductIds = [];
  let recommendationSourceIds = [];
  let lovedProductNames = {};
  let completedCalls = 0;
  const existingPlaylistRatingState = window.vinpopPlaylistRatingFilter || {};
  let playlistRatingBuckets = existingPlaylistRatingState.ready && existingPlaylistRatingState.buckets
    ? existingPlaylistRatingState.buckets
    : {
    favorite: new Set(),
    excellent: new Set(),
    adventure: new Set()
  };

  window.vinpopPlaylistRatingFilter = existingPlaylistRatingState;
  window.vinpopPlaylistRatingFilter.active = existingPlaylistRatingState.active || '';
  window.vinpopPlaylistRatingFilter.buckets = playlistRatingBuckets;
  window.vinpopPlaylistRatingFilter.ready = existingPlaylistRatingState.ready === true;

  function getProductGrid() {
    return document.querySelector('#facets-results ul.grid') ||
      document.querySelector('#facets-results ul') ||
      document.querySelector('ul.grid.gap-theme') ||
      document.querySelector('ul.grid');
  }

  function getProductCards(productGrid) {
    if (!productGrid) return [];
    return Array.from(productGrid.querySelectorAll('li[data-product-id]'));
  }

  function hideSimilarityLegend() {
    document.querySelectorAll('.rte, .text-rte').forEach((element) => {
      const text = String(element.textContent || '');
      const isSimilarityLegend = text.includes('Only professional tasters perceive the difference') ||
        text.includes('Wine connoisseurs also perceive the difference') ||
        text.includes('Very close taste profile') ||
        text.includes('Different taste');

      if (!isSimilarityLegend) return;

      const block = element.closest('.rte') || element;
      block.style.setProperty('display', 'none', 'important');
      block.setAttribute('aria-hidden', 'true');
    });
  }

  function showFriendPlaylistAccessDenied() {
    if (document.querySelector('.vinpop-friend-playlist-denied')) {
      return;
    }

    const target = document.querySelector('#facets-results') || document.querySelector('main') || document.body;
    const productGrid = getProductGrid();
    if (productGrid) {
      productGrid.style.display = 'none';
    }

    const isEnglish = (document.documentElement.lang || '').toLowerCase().indexOf('en') === 0;
    const message = document.createElement('div');
    message.className = 'vinpop-friend-playlist-denied';
    message.style.cssText = 'max-width:720px;margin:2rem auto;padding:1.25rem;border:2px solid #000;background:#fff7ed;text-align:center;';
    message.innerHTML = '<h2 class="font-heading heading-standard mb-2">'
      + (isEnglish ? 'This friend playlist is not available' : 'Deze vriendenplaylist is niet beschikbaar')
      + '</h2><p>'
      + (isEnglish
        ? 'You may not be friends with this user, or this friend has not enabled playlist sharing.'
        : 'Je bent mogelijk geen vrienden met deze gebruiker, of deze vriend heeft playlist delen niet ingeschakeld.')
      + '</p>';

    if (target.id === 'facets-results') {
      target.innerHTML = '';
      target.appendChild(message);
      return;
    }

    target.insertBefore(message, target.firstChild);
  }

  async function fetchDistanceForWine(wineId) {
    const response = await fetch('https://api.vinpop.fr/api/wines/getDistance', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: String(wineId) })
    });

    if (!response.ok) {
      throw new Error('distance_fetch_failed');
    }

    return response.json();
  }

  if (isDemoMode) {
    // Demo loved wine IDs with their food pairing categories
    const demoWines = [
      { id: '15510535995779', pairings: { red_meat: true, white_meat: false, fish_seafood: false, cheese: false, aperitif: false } },
      { id: '15513163760003', pairings: { red_meat: false, white_meat: false, fish_seafood: false, cheese: true, aperitif: false } },
      { id: '15513442386307', pairings: { red_meat: false, white_meat: true, fish_seafood: false, cheese: false, aperitif: false } },
      { id: '15513223037315', pairings: { red_meat: false, white_meat: false, fish_seafood: true, cheese: false, aperitif: false } },
      { id: '15512871141763', pairings: { red_meat: false, white_meat: false, fish_seafood: false, cheese: false, aperitif: true } }
    ];

    lovedProductIds = demoWines.map(w => w.id);
    excellentProductIds = [];
    recommendationSourceIds = [...lovedProductIds];
    pairingsData = demoWines.map(w => ({ wine_id: w.id, ...w.pairings }));

    // Get product names from DOM
    lovedProductIds.forEach(productId => {
      const productElement = document.querySelector(`li[data-product-id="${productId}"]`);
      if (productElement) {
        const productLink = productElement.querySelector('h3');
        if (productLink) {
          lovedProductNames[productId] = productLink.textContent.trim();
        }
      }
    });

    // In demo mode, call getDistance API for each demo loved wine (API doesn't require auth)
    recommendationSourceIds.forEach(async (lovedProductId) => {
      try {
        const response = await fetchDistanceForWine(lovedProductId);
        if (response && response.length) {
          allApiResponses.push({ response: response, sourceProductId: lovedProductId, sourceRating: 'love' });
        }
      } catch (error) {
        console.error('Demo mode: Error fetching comparisons for wine ' + lovedProductId);
      } finally {
        completedCalls++;
        if (completedCalls === recommendationSourceIds.length) {
          processAllRecommendations();
        }
      }
    });

    // Initialize filter buttons so food pairing filter works in demo too
    initializeFilterButtons();
    initializePlaylistRatingFilterButtons();
    initializeFoodPairingAccordion();
    
    // Skip loadAllData (we injected demo data instead)
    // Reset flag after a delay
    setTimeout(() => { isInitializing = false; }, 1000);
    // Don't call loadAllData, but we still need processAllRecommendations etc.
    // The rest of the code below (loadAllData, processAllRecommendations, etc.) is defined and reused.
    // We skip the loadAllData() call at the end by returning early after demo setup, but we need
    // the function definitions. So we let them be defined and just prevent the loadAllData() call.
  }

  // Load all data (only called for logged-in users, not in demo mode)
  async function loadAllData() {
    try {
      updateFriendPlaylistHeading();

      // Load food pairings for customer's rated wines
      if (friendId) {
        pairingsData = [];
      } else {
        const pairingsResponse = await fetch(`https://api.vinpop.fr/api/pairings/getAll?customerId=${customerId}`, {
          headers: buildFriendsAuthHeaders()
        });
        pairingsData = await pairingsResponse.json();
      }
      // console.log(`📊 Loaded ${pairingsData.length} food pairings`);

      // Load ratings to get loved wines (rating = "3")
      const ratingsArray = await loadPlaylistRatings();

      if (!ratingsArray || ratingsArray.length === 0) {
        // console.log('No ratings found');
        initializeFilterButtons();
        initializePlaylistRatingFilterButtons();
        initializeFoodPairingAccordion();
        updatePlaylistRatingBuckets();
        return;
      }

      // Get all loved products (rating = 3)
      lovedProductIds = ratingsArray
        .filter(item => String(item.rating) === "3")
        .map(item => item.id);

      // Rating 2 is the "Excellent" tier in the playlist logic.
      excellentProductIds = ratingsArray
        .filter(item => String(item.rating) === "2")
        .map(item => item.id);

      recommendationSourceIds = [...new Set([...lovedProductIds, ...excellentProductIds].map(String))];

      // Count how many have food pairing data (for logging purposes)
      const lovedWithPairings = lovedProductIds.filter(productId => {
        return pairingsData.some(p => String(p.wine_id) === String(productId));
      });

      // console.log(`❤️ Found ${lovedProductIds.length} total loved wines`);
      // console.log(`🍷 ${lovedWithPairings.length} of them have food pairing data`);

      if (recommendationSourceIds.length === 0) {
        // console.log('No rated products found');
        initializeFilterButtons();
        initializePlaylistRatingFilterButtons();
        initializeFoodPairingAccordion();
        updatePlaylistRatingBuckets();
        return;
      }

      // Get names of rated products from DOM so recommendation messages can cite
      // either a loved wine or a wine rated excellent.
      recommendationSourceIds.forEach(productId => {
        const productElement = document.querySelector(`li[data-product-id="${productId}"]`);
        if (productElement) {
          const productLink = productElement.querySelector('h3');
          if (productLink) {
            lovedProductNames[productId] = productLink.textContent.trim();
          }
        }
      });

      // Load wine comparisons for each loved wine
      recommendationSourceIds.forEach(async (sourceProductId) => {
        try {
          const response = await fetchDistanceForWine(sourceProductId);
          if (response && response.length) {
            allApiResponses.push({
              response: response,
              sourceProductId: sourceProductId,
              sourceRating: lovedProductIds.map(String).includes(String(sourceProductId)) ? 'love' : 'excellent'
            });
          }
        } catch (error) {
          console.error('Error fetching comparisons for wine ' + sourceProductId, error);
        } finally {
          completedCalls++;
          if (completedCalls === (recommendationSourceIds || []).length) {
            processAllRecommendations();
          }
        }
      });

    } catch (error) {
      console.error('❌ Error loading data:', error);
    }
  }

  // Process all recommendations
  function processAllRecommendations() {
    if (completedCalls !== (recommendationSourceIds || []).length) return;

    const productGrid = getProductGrid();
    if (!productGrid) {
      // The collection can be rendered late by Shopify/AJAX.
      // Retry once the grid is present instead of giving up permanently.
      setTimeout(() => {
        isInitializing = false;
        if (!hasAppliedRecommendations) initFoodPairingFilter();
      }, 400);
      return;
    }

    const allProducts = getProductCards(productGrid);
    if (allProducts.length === 0) {
      setTimeout(() => {
        isInitializing = false;
        if (!hasAppliedRecommendations) initFoodPairingFilter();
      }, 400);
      return;
    }
    // console.log(`📦 Found ${allProducts.length} total products in grid`);

    // Build compatibility map
    const productCompatibilityMap = buildCompatibilityMap(recommendationSourceIds || []);
    updatePlaylistRatingBuckets();

    // Create sorted product array with recommendations
    const productsWithCompatibility = buildProductArray(allProducts, productCompatibilityMap, lovedProductIds || []);

    // Sort by compatibility
    productsWithCompatibility.sort((a, b) => b.compatibility - a.compatibility);

    // Reorder products in DOM
    allProducts.forEach(product => product.remove());

    productsWithCompatibility.forEach(item => {
      productGrid.appendChild(item.element);
      addRecommendationDiv(item.element, item.message, item.messageType);
    });

    // Add remaining products without recommendations
    allProducts.forEach(productElement => {
      const productId = productElement.getAttribute('data-product-id');
      const hasRecommendation = productsWithCompatibility.some(item => item.productId === productId);
      if (!hasRecommendation) {
        productGrid.appendChild(productElement);
      }
    });

    // console.log(`✅ Applied ${productsWithCompatibility.length} wine recommendations`);

    // Apply initial filter
    applyCurrentFilter();
    hasAppliedRecommendations = true;
    
    // Align product cards after reorganization (short delay to ensure DOM is settled)
    setTimeout(() => {
      if (typeof window.alignProductCards === 'function') {
        window.alignProductCards();
      }
    }, 100);
  }

  // Build compatibility map from API responses
  function buildCompatibilityMap(filteredSourceWines) {
    const productCompatibilityMap = {};
    const filteredSourceIds = (filteredSourceWines || []).map(String);

    allApiResponses.forEach(({ response, sourceProductId }) => {
      // Skip if this source wine doesn't match the current filter.
      if (!filteredSourceIds.includes(String(sourceProductId))) return;
      if (!response || !response.length) return;

      response.forEach(wine => {
        [wine.productid1, wine.productid2].forEach(productId => {
          if ((lovedProductIds || []).map(String).includes(String(productId))) return;

          const distance = wine.perceptive_distance || wine.distance;
          const compatibility = Math.max(0, Math.floor(100 - distance));

          if (!productCompatibilityMap[productId] || compatibility > productCompatibilityMap[productId].compatibility) {
            productCompatibilityMap[productId] = {
              compatibility: compatibility,
              sourceProductId: sourceProductId,
              sourceProductName: lovedProductNames[sourceProductId] || '',
              sourceRating: (excellentProductIds || []).map(String).includes(String(sourceProductId)) ? 'excellent' : 'love'
            };
          }
        });
      });
    });

    return productCompatibilityMap;
  }

  function getComparedProductId(wine, sourceProductId) {
    const source = String(sourceProductId);
    const id1 = String(wine.productid1 || '');
    const id2 = String(wine.productid2 || '');

    if (id1 === source && id2) return id2;
    if (id2 === source && id1) return id1;
    return id1 || id2;
  }

  function addBestScore(scoreMap, productId, score) {
    const id = String(productId || '');
    if (!id || !Number.isFinite(score)) return;
    if (!scoreMap[id] || score > scoreMap[id]) {
      scoreMap[id] = score;
    }
  }

  function updatePlaylistRatingBuckets() {
    const loveIds = new Set((lovedProductIds || []).map(String));
    const excellentIds = new Set((excellentProductIds || []).map(String));
    const loveScores = {};
    const excellentScores = {};

    loveIds.forEach((productId) => addBestScore(loveScores, productId, 100));
    excellentIds.forEach((productId) => addBestScore(excellentScores, productId, 100));

    allApiResponses.forEach(({ response, sourceProductId, sourceRating }) => {
      if (!response || !response.length) return;

      response.forEach((wine) => {
        const productId = getComparedProductId(wine, sourceProductId);
        if (!productId) return;

        const distance = Number(wine.perceptive_distance || wine.distance);
        if (!Number.isFinite(distance)) return;

        const compatibility = Math.max(0, Math.floor(100 - distance));
        if (sourceRating === 'love') {
          addBestScore(loveScores, productId, compatibility);
        }
        if (sourceRating === 'excellent') {
          addBestScore(excellentScores, productId, compatibility);
        }
      });
    });

    const favorite = new Set();
    const excellent = new Set();
    const adventure = new Set();
    const allIds = new Set([
      ...Object.keys(loveScores),
      ...Object.keys(excellentScores),
      ...loveIds,
      ...excellentIds
    ]);

    allIds.forEach((productId) => {
      const loveScore = loveScores[productId] || 0;
      const excellentScore = excellentScores[productId] || 0;
      const isAlreadyRated = loveIds.has(productId) || excellentIds.has(productId);
      const isExcellentOnlyRating = excellentIds.has(productId) && !loveIds.has(productId);

      if (!isExcellentOnlyRating && loveScore >= 94 && loveScore <= 100) {
        favorite.add(productId);
      }

      if (!favorite.has(productId) && ((excellentScore >= 94 && excellentScore <= 100) || (loveScore >= 88 && loveScore <= 93))) {
        excellent.add(productId);
      }

      if (!favorite.has(productId) && !excellent.has(productId) && !isAlreadyRated && ((loveScore >= 80 && loveScore <= 97) || (excellentScore >= 80 && excellentScore <= 87))) {
        adventure.add(productId);
      }
    });

    playlistRatingBuckets = { favorite, excellent, adventure };
    window.vinpopPlaylistRatingFilter = window.vinpopPlaylistRatingFilter || {};
    window.vinpopPlaylistRatingFilter.buckets = playlistRatingBuckets;
    window.vinpopPlaylistRatingFilter.active = window.vinpopPlaylistRatingFilter.active || '';
    window.vinpopPlaylistRatingFilter.ratedIds = new Set([...loveIds, ...excellentIds]);
    window.vinpopPlaylistRatingFilter.ready = true;
    document.dispatchEvent(new CustomEvent('vinpop:playlist-rating-buckets-ready', {
      detail: {
        favorite: favorite.size,
        excellent: excellent.size,
        adventure: adventure.size
      }
    }));
  }

  // Build array of products with compatibility data
  function buildProductArray(allProducts, productCompatibilityMap, filteredLovedWines) {
    const productsWithCompatibility = [];

    function getLovedWineMessage() {
      return friendId ? 'YOUR FRIEND LOVED IT' : getTranslation('you_loved_it');
    }

    function getLikedWineMessage() {
      if (friendId) return 'YOUR FRIEND LIKED IT';
      const translated = getTranslation('you_liked_it');
      if (translated && translated !== 'you_liked_it') return translated;
      return (document.documentElement.lang || '').toLowerCase().indexOf('nl') === 0
        ? 'Je vond het excellent'
        : 'You liked it';
    }

    function getBecauseCopy(sourceRating) {
      const isDutch = (document.documentElement.lang || '').toLowerCase().indexOf('nl') === 0;
      if (sourceRating === 'excellent') {
        return isDutch ? 'omdat je deze excellent vond' : 'because you liked';
      }
      return isDutch ? 'omdat je hield van' : 'because you loved';
    }

    const filteredLovedWineIds = (filteredLovedWines || []).map(String);
    const excellentIds = (excellentProductIds || []).map(String);

    allProducts.forEach(productElement => {
      const productId = String(productElement.getAttribute('data-product-id') || '');

      if (filteredLovedWineIds.includes(productId)) {
        productsWithCompatibility.push({
          element: productElement,
          compatibility: 100,
          message: getLovedWineMessage(),
          messageType: 'rated-love',
          productId: productId
        });
      } else if (excellentIds.includes(productId)) {
        productsWithCompatibility.push({
          element: productElement,
          compatibility: 99,
          message: getLikedWineMessage(),
          messageType: 'rated-excellent',
          productId: productId
        });
      } else if (productCompatibilityMap[productId]) {
        const compatData = productCompatibilityMap[productId];
        const becauseCopy = getBecauseCopy(compatData.sourceRating);
        productsWithCompatibility.push({
          element: productElement,
          compatibility: compatData.compatibility,
          message: `${becauseCopy} ${compatData.sourceProductName}`,
          messageType: 'recommendation',
          productId: productId
        });
      }
    });

    return productsWithCompatibility;
  }

  // Same match palette as the Smart Wine Box builder (love / like / adventure).
  const PLAYLIST_BUCKET_BADGES = {
    favorite: { background: '#c2412d', color: '#ffffff' },
    excellent: { background: '#347a32', color: '#ffffff' },
    adventure: { background: '#315fbd', color: '#ffffff' }
  };

  function getPlaylistBucket(productId) {
    const id = String(productId || '');
    if (!id || !playlistRatingBuckets) return '';

    return ['favorite', 'excellent', 'adventure'].find((bucket) => {
      const ids = playlistRatingBuckets[bucket];
      if (ids instanceof Set) return ids.has(id);
      if (Array.isArray(ids)) return ids.map(String).includes(id);
      return false;
    }) || '';
  }

  function getPlaylistBucketLabel(bucket) {
    const isDutch = (document.documentElement.lang || '').toLowerCase().indexOf('nl') === 0;

    if (bucket === 'adventure') {
      return isDutch ? 'Nieuwe avonturen' : 'New adventures';
    }

    const key = bucket === 'favorite' ? 'you_will_love_it' : 'you_will_like_it';
    const translated = getTranslation(key);
    if (translated && translated !== key) return translated;

    if (bucket === 'favorite') {
      return isDutch ? 'Je zult het heerlijk vinden' : 'You will love it';
    }
    return isDutch ? 'Je zult het lekker vinden' : 'You will like it';
  }

  function createCardBadge(label, background, color) {
    const badge = document.createElement('div');
    badge.className = 'wine-recommendation pointer-events-none';
    badge.style.cssText = 'position:absolute;top:8px;right:8px;min-width:78px;max-width:96px;min-height:48px;border-radius:999px;' +
      'background:' + background + ';color:' + color + ';display:flex;align-items:center;justify-content:center;padding:0.45rem 0.55rem;' +
      "text-align:center;font-family:'Prompt',sans-serif;" +
      'box-shadow:0 3px 10px rgba(0,0,0,0.18);' +
      'transform:rotate(3deg);' +
      'z-index:30;pointer-events:none;' +
      'border:2px solid rgba(0,0,0,0.15);';
    badge.innerHTML = `<span style="font-size:12px;font-weight:800;line-height:1.05;text-align:center;">${label}</span>`;
    return badge;
  }

  // Add recommendation badge on product image AND text below the card
  function addRecommendationDiv(productElement, message, messageType) {
    // Safety: Only proceed if valid message exists
    if (!message) return false;

    // Find the product image link (parent of the image)
    const imageLink = productElement.querySelector('.product-tile__featured-media');

    // Remove existing recommendation badge if any
    const existingRec = productElement.querySelector('.wine-recommendation');
    if (existingRec) {
      existingRec.remove();
    }
    
    // Remove existing recommendation text if any
    const existingText = productElement.querySelector('.wine-recommendation-text');
    if (existingText) {
      existingText.remove();
    }

    const isLovedWine = messageType === 'rated-love';
    const isLikedWine = messageType === 'rated-excellent';
    const isRatedWine = isLovedWine || isLikedWine;

    // Save match data to sessionStorage so product pages can show the badge
    try {
      const productId = productElement.getAttribute('data-product-id');
      if (productId) {
        const existingMatches = JSON.parse(sessionStorage.getItem('vinpop_product_matches') || '{}');
        const sourceMatch = message.match(/(?:because you loved|because you liked|omdat je hield van|omdat je deze excellent vond)\s+(.+)$/i);
        const sourceProductName = sourceMatch ? sourceMatch[1].trim() : null;
        existingMatches[productId] = {
          score: null,
          message: message,
          isLovedWine: isRatedWine,
          sourceProductName: sourceProductName
        };
        sessionStorage.setItem('vinpop_product_matches', JSON.stringify(existingMatches));
      }
    } catch(e) {}

    if (isRatedWine && imageLink) {
      imageLink.style.position = 'relative';
      imageLink.appendChild(createCardBadge(message, '#E07A5F', '#fff'));
    }

    // Not rated yet: predict how the wine will land, using the same buckets the
    // playlist filter uses so badge and filter always tell the same story.
    if (!isRatedWine && imageLink) {
      const bucket = getPlaylistBucket(productElement.getAttribute('data-product-id'));
      const badgeStyle = PLAYLIST_BUCKET_BADGES[bucket];
      if (badgeStyle) {
        imageLink.style.position = 'relative';
        imageLink.appendChild(createCardBadge(getPlaylistBucketLabel(bucket), badgeStyle.background, badgeStyle.color));
      }
    }

    // Add text below the card with the relationship, without exposing lab percentages.
    if (!isRatedWine) {
      const quickBuySection = productElement.querySelector('.product-tile__quick-buy');
      if (quickBuySection) {
        const textDiv = document.createElement('div');
        textDiv.className = 'wine-recommendation-text';
        textDiv.style.cssText = `
          margin-top: 0.75rem;
          padding: 0 0.5rem;
          text-align: center;
          min-height: 3.5rem;
        `;
        
        const mainLine = document.createElement('div');
        mainLine.style.cssText = `
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: rgb(var(--color-scheme-text));
          line-height: 1.3;
          text-align: center;
        `;
        mainLine.textContent = message;
        
        textDiv.appendChild(mainLine);
        
        // Insert before quick-buy section
        quickBuySection.parentNode.insertBefore(textDiv, quickBuySection);
      }
    }
    
    // Add text below the card for rated wines.
    if (isRatedWine) {
      const quickBuySection = productElement.querySelector('.product-tile__quick-buy');
      if (quickBuySection) {
        const textDiv = document.createElement('div');
        textDiv.className = 'wine-recommendation-text';
        textDiv.style.cssText = `
          margin-top: 0.75rem;
          padding: 0 0.5rem;
          text-align: center;
          min-height: 3.5rem;
        `;
        
        const mainLine = document.createElement('div');
        mainLine.style.cssText = `
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: rgb(var(--color-scheme-text));
          line-height: 1.3;
          text-align: center;
        `;
        mainLine.textContent = message;
        
        textDiv.appendChild(mainLine);
        
        // Insert before quick-buy section
        quickBuySection.parentNode.insertBefore(textDiv, quickBuySection);
      }
    }

    const hasVisibleNote = !!productElement.querySelector('.wine-recommendation, .wine-recommendation-text');
    productElement.dataset.vinpopHasPlaylistNote = hasVisibleNote ? 'true' : 'false';
    return hasVisibleNote;
  }

  // Apply current filter
  function applyCurrentFilter() {
    const activeButton = document.querySelector('.food-pairing-btn[aria-pressed="true"]');
    const activePairing = activeButton ? activeButton.getAttribute('data-pairing') : 'all';
    filterProducts(activePairing);
  }

  // Filter products by food pairing and recalculate recommendations
  function filterProducts(pairing) {
    const productGrid = getProductGrid();
    if (!productGrid) return;

    // Check if we're in taste profile mode
    const urlParams = new URLSearchParams(window.location.search);
    const inTasteProfileMode = urlParams.get('taste_profile') === 'true';

    let allProductElements;
    if (inTasteProfileMode) {
      // Only get products that are NOT hidden by the quiz
      allProductElements = Array.from(productGrid.querySelectorAll('li[data-product-id]:not(.taste-hidden)'));
      // console.log('🍷 Taste profile mode: filtering only quiz-recommended wines');
    } else {
      allProductElements = Array.from(productGrid.querySelectorAll('li[data-product-id]'));
    }

    // Filter recommendation sources by selected pairing. The three playlist
    // buckets remain the master filter; Food Pairing only narrows the source set.
    let filteredSourceWines = [];
    let filteredLovedWines = [];
    if (pairing === 'all') {
      filteredSourceWines = [...(recommendationSourceIds || [])];
      filteredLovedWines = [...(lovedProductIds || [])];
    } else {
      filteredLovedWines = (lovedProductIds || []).filter(lovedId => {
        const wineData = pairingsData.find(p => String(p.wine_id) === String(lovedId));
        return wineData && wineData[pairing] === true;
      });
      filteredSourceWines = [...filteredLovedWines];
    }

    // console.log(`🔍 Filtering by ${pairing}: ${filteredLovedWines.length} loved wines match this pairing`);

    // Recalculate compatibility map with filtered source wines
    const productCompatibilityMap = buildCompatibilityMap(filteredSourceWines);

    // Rebuild products array with new compatibility scores
    const productsWithCompatibility = buildProductArray(allProductElements, productCompatibilityMap, filteredLovedWines);

    // Sort by compatibility
    productsWithCompatibility.sort((a, b) => b.compatibility - a.compatibility);

    // Reorder products in DOM
    allProductElements.forEach(product => product.remove());

    const eligibleProductIds = new Set();

    productsWithCompatibility.forEach(item => {
      productGrid.appendChild(item.element);
      if (addRecommendationDiv(item.element, item.message, item.messageType)) {
        eligibleProductIds.add(String(item.productId));
      }
    });

    // Add remaining products without recommendations. They stay in the DOM so
    // Shopify/native filters can still work, but the playlist filter decides
    // whether they are visible.
    allProductElements.forEach(productElement => {
      const productId = productElement.getAttribute('data-product-id');
      const hasRecommendation = productsWithCompatibility.some(item => item.productId === productId);
      if (!hasRecommendation) {
        productGrid.appendChild(productElement);
        const existingRec = productElement.querySelector('.wine-recommendation');
        if (existingRec) existingRec.remove();
        const existingText = productElement.querySelector('.wine-recommendation-text');
        if (existingText) existingText.remove();
        productElement.dataset.vinpopHasPlaylistNote = 'false';
      }
    });

    window.vinpopFoodPairingFilter = {
      active: pairing,
      ready: true,
      eligibleProductIds: eligibleProductIds
    };

    updateFilteredCount(enforcePlaylistAndFoodPairingVisibility(allProductElements, pairing, eligibleProductIds));
    // console.log(`✅ Reordered products with recommendations filtered by ${pairing}`);
    
    // Dispatch custom event to notify that DOM has been reorganized
    document.dispatchEvent(new CustomEvent('vinpop:products-reordered'));

    if (typeof window.vinpopMyWinePlaylistApplyFilters === 'function') {
      window.vinpopMyWinePlaylistApplyFilters();
    }
  }

  // Update filtered count display
  function updateFilteredCount(count) {
    const filteredCountElement = document.querySelector('#filtered-count, .filtered-count');
    if (filteredCountElement) {
      filteredCountElement.textContent = count;
    }
  }

  function productIsInActivePlaylistBucket(productId) {
    const state = window.vinpopPlaylistRatingFilter;
    const active = state && state.active ? String(state.active) : '';
    if (!active || !state.ready || !state.buckets) return true;

    const bucket = state.buckets[active];
    if (bucket instanceof Set) return bucket.has(String(productId));
    if (Array.isArray(bucket)) return bucket.map(String).includes(String(productId));
    return false;
  }

  function enforcePlaylistAndFoodPairingVisibility(productElements, pairing, eligibleProductIds) {
    return productElements.reduce((count, productElement) => {
      const productId = String(productElement.getAttribute('data-product-id') || '');
      const matchesPlaylist = productId && productIsInActivePlaylistBucket(productId);
      const matchesFoodPairing = eligibleProductIds.has(productId);
      const shouldHide = !matchesPlaylist || !matchesFoodPairing;

      if (shouldHide) {
        productElement.dataset.vinpopFoodHidden = 'true';
        productElement.style.setProperty('display', 'none', 'important');
        productElement.setAttribute('aria-hidden', 'true');
        return count;
      }

      if (productElement.dataset.vinpopFoodHidden === 'true') {
        delete productElement.dataset.vinpopFoodHidden;
        productElement.style.removeProperty('display');
        productElement.removeAttribute('aria-hidden');
      }

      return count + 1;
    }, 0);
  }

  function refreshActiveFoodPairingFilter() {
    const activeButton = document.querySelector('.food-pairing-btn[aria-pressed="true"]');
    const activePairing = activeButton ? activeButton.getAttribute('data-pairing') : 'all';
    filterProducts(activePairing || 'all');
  }

  // Initialize filter buttons
  function initializeFilterButtons() {
    const filterButtons = document.querySelectorAll('.food-pairing-btn');
    filterButtons.forEach(button => {
      if (button.dataset.vinpopFoodPairingBound === 'true') return;
      button.dataset.vinpopFoodPairingBound = 'true';
      button.addEventListener('click', function() {
        filterButtons.forEach(btn => btn.setAttribute('aria-pressed', 'false'));
        this.setAttribute('aria-pressed', 'true');
        filterProducts(this.getAttribute('data-pairing'));
        // Re-align after filter change
        setTimeout(() => {
          if (typeof window.alignProductCards === 'function') {
            window.alignProductCards();
          }
        }, 100);
      });
    });
  }

  function applyPlaylistRatingFilter() {
    if (typeof window.vinpopMyWinePlaylistApplyFilters === 'function') {
      window.vinpopMyWinePlaylistApplyFilters();
      return;
    }

    document.dispatchEvent(new CustomEvent('vinpop:playlist-rating-filter-change', {
      detail: {
        active: window.vinpopPlaylistRatingFilter?.active || ''
      }
    }));
  }

  function updatePlaylistRatingButtonCounts() {
    const state = window.vinpopPlaylistRatingFilter;
    if (!state || !state.ready || !state.buckets) return;

    // Buckets can hold wines that are not in this collection, so count against the grid.
    const gridProductIds = getProductCards(getProductGrid()).map((card) => String(card.getAttribute('data-product-id')));

    document.querySelectorAll('[data-playlist-rating-filter]').forEach((button) => {
      const key = button.getAttribute('data-playlist-rating-filter');
      const title = button.querySelector('.playlist-rating-filter__title');
      if (!title) return;
      if (!title.dataset.baseText) {
        title.dataset.baseText = title.textContent.replace(/\s+\(\d+\)$/, '').trim();
      }
      const bucket = state.buckets[key];
      const count = bucket ? gridProductIds.filter((productId) => bucket.has(productId)).length : 0;
      const currentCountMatch = title.textContent.match(/\((\d+)\)\s*$/);
      if (count === 0 && currentCountMatch && Number(currentCountMatch[1]) > 0) {
        return;
      }
      title.textContent = `${title.dataset.baseText} (${count})`;
    });
  }

  function refreshPlaylistRatingButtons() {
    const active = window.vinpopPlaylistRatingFilter?.active || '';
    document.querySelectorAll('[data-playlist-rating-filter]').forEach((button) => {
      const key = button.getAttribute('data-playlist-rating-filter');
      button.setAttribute('aria-pressed', key === active ? 'true' : 'false');
    });
    updatePlaylistRatingButtonCounts();
  }

  function initializePlaylistRatingFilterButtons() {
    const buttons = document.querySelectorAll('[data-playlist-rating-filter]');
    buttons.forEach((button) => {
      if (button.dataset.vinpopPlaylistRatingBound === 'true') return;
      button.dataset.vinpopPlaylistRatingBound = 'true';

      button.addEventListener('click', function() {
        const key = this.getAttribute('data-playlist-rating-filter') || '';
        const state = window.vinpopPlaylistRatingFilter || {};
        state.active = state.active === key ? '' : key;
        window.vinpopPlaylistRatingFilter = state;
        refreshPlaylistRatingButtons();
        applyPlaylistRatingFilter();
        refreshActiveFoodPairingFilter();
      });
    });

    refreshPlaylistRatingButtons();
  }

  function initializeFoodPairingAccordion() {
    const toggle = document.querySelector('.food-pairing-accordion-toggle');
    const panel = document.getElementById('food-pairing-accordion-panel');
    if (!toggle || !panel || toggle.dataset.vinpopAccordionBound === 'true') return;

    toggle.dataset.vinpopAccordionBound = 'true';
    toggle.addEventListener('click', function() {
      const expanded = this.getAttribute('aria-expanded') === 'true';
      this.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      panel.hidden = expanded;
    });
  }

  if (!window.__vinpopPlaylistRatingBucketsListenerBound) {
    window.__vinpopPlaylistRatingBucketsListenerBound = true;
    document.addEventListener('vinpop:playlist-rating-buckets-ready', refreshPlaylistRatingButtons);
  }

  if (!window.__vinpopFoodPairingPlaylistSyncBound) {
    window.__vinpopFoodPairingPlaylistSyncBound = true;
    document.addEventListener('vinpop:playlist-rating-filter-change', refreshActiveFoodPairingFilter);
  }

  // Initialize
  hideSimilarityLegend();

  if (!isDemoMode) {
    initializeFilterButtons();
    initializePlaylistRatingFilterButtons();
    initializeFoodPairingAccordion();
    loadAllData();
  }
  
  // Reset flag after a delay to allow re-initialization later
  if (!isDemoMode) {
    setTimeout(() => {
      isInitializing = false;
    }, 1000);
  }
}

// Run on page load
document.addEventListener('DOMContentLoaded', function() {
  // Check if we should initialize
  const pathname = window.location.pathname;
  const urlParams = new URLSearchParams(window.location.search);
  const isWineCollection = isMyWinePlaylistPage(pathname);
  const hasSourceProduct = urlParams.get('source_product') || urlParams.get('main-product');
  
  // Only initialize if on /collections/my-wine-playlist WITHOUT source_product
  if (isWineCollection && !hasSourceProduct) {
    initFoodPairingFilter();
    
    // Do not observe the whole main tree here. This script reorders product
    // cards itself; observing those mutations creates a feedback loop.
  }
});

// Re-run when Shopify reloads collection section in theme editor
document.addEventListener('shopify:section:load', function(event) {
  // console.log('🔄 Section reloaded in theme editor, re-initializing food pairing filter...');
  setTimeout(initFoodPairingFilter, 100);
});
